import type { KeyboardInfo } from "@capacitor/keyboard";
import { Keyboard, KeyboardResize, KeyboardStyle } from "@capacitor/keyboard";
import { Cubit } from "blac";
import { isAndroid, isHybridApp, isIOS } from "src/lib/platform";

interface KeyboardState {
  keyboardHeight: number;
  isOpen: boolean;
}

interface VirtualKeyboard extends EventTarget {
  overlaysContent: boolean;
  boundingRect: DOMRectReadOnly;
}

// Height difference below this is likely browser chrome (address bar), not a keyboard
const KEYBOARD_THRESHOLD = 100;

// iOS Safari is slow to dismiss the keyboard and update viewport values
const FOCUS_OUT_DELAY_MS = 300;

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "password",
  "number",
  ""
]);

function isTextInput(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const tag = el.tagName;
  if (tag === "TEXTAREA") {
    return true;
  }
  if (tag === "INPUT") {
    return TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
  }
  return el.isContentEditable;
}

// Walk up to the nearest ancestor that actually scrolls (e.g. InDialog's
// Content, overflow-y: auto). Dialogs scroll their own container, not the
// body, so scrolling document.body would be a no-op.
function getScrollableParent(el: HTMLElement | null): HTMLElement {
  for (let node = el; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    const canScroll = overflowY === "auto" || overflowY === "scroll";
    if (canScroll && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.body;
}

export default class KeyboardBloc extends Cubit<KeyboardState> {
  constructor() {
    super({ keyboardHeight: 0, isOpen: false });
    this.setupListenersForNative();
    this.setupListenersForWeb();
  }

  setupListenersForNative = (): void => {
    if (!isHybridApp()) {
      return;
    }

    void Keyboard.addListener("keyboardWillShow", this.keyboardWillShow);
    void Keyboard.addListener("keyboardWillHide", this.keyboardWillHide);
    void Keyboard.addListener("keyboardDidShow", this.addPagePadding);

    if (isIOS()) {
      void Keyboard.setResizeMode({ mode: KeyboardResize.None });
      void Keyboard.setStyle({ style: KeyboardStyle.Default });
    }
  };

  addPagePadding = (): void => {
    // keyboardDidShow is more reliable than keyboardWillShow on some Android
    // OEM keyboards (e.g. Samsung), so reassert the class here too rather
    // than only from keyboardWillShow.
    if (isAndroid()) {
      document.body.classList.toggle(
        "keyboard-open-android",
        this.state.isOpen
      );
    }

    document.body.style.setProperty(
      "--keyboard-height",
      `${this.state.keyboardHeight}px`
    );

    if (this.state.keyboardHeight !== 0 || isHybridApp()) {
      document.body.style.setProperty(
        "--stored-keyboard-height",
        `${this.state.keyboardHeight}px`
      );
    }

    setTimeout(() => {
      this.scrollInputIntoView();
    });
  };

  scrollInputIntoView = (): void => {
    const focusedElement = document.activeElement as HTMLElement;
    if (!focusedElement) {
      return;
    }

    const elementScreenPosition = focusedElement.getBoundingClientRect().top;
    const distanceFromKeyboard = window.innerHeight - elementScreenPosition;
    const isElementHiddenByKeyboard =
      distanceFromKeyboard - 50 < this.state.keyboardHeight;

    const amountToScroll =
      this.state.keyboardHeight -
      distanceFromKeyboard +
      window.innerHeight * 0.3;

    const closestScrollableParent = getScrollableParent(focusedElement);

    if (isElementHiddenByKeyboard) {
      closestScrollableParent.scrollTop += amountToScroll;
    }
  };

  removePagePadding = (): void => {
    document.body.style.setProperty("--keyboard-height", "0px");
    if (isHybridApp()) {
      document.body.style.setProperty("--stored-keyboard-height", "0px");
    }
  };

  keyboardWillShow = (event: KeyboardInfo): void => {
    // On Android, SystemBars already pads the WebView above the keyboard
    // natively, so reporting the keyboard height again here would double it.
    const keyboardHeight = isAndroid() ? 0 : event.keyboardHeight;
    this.emit({ keyboardHeight, isOpen: true });
    document.body.classList.add("keyboard-open");
    this.addPagePadding();
  };

  keyboardWillHide = (): void => {
    this.emit({ keyboardHeight: 0, isOpen: false });
    document.body.classList.remove("keyboard-open");
    document.body.classList.remove("keyboard-open-android");
    this.removePagePadding();
  };

  // ── Web ────────────────────────────────────────

  private initialViewportHeight = 0;
  private inputFocused = false;
  private focusOutTimer: ReturnType<typeof setTimeout> | null = null;

  setupListenersForWeb = (): void => {
    // Android's keyboardWillShow/DidShow only fire when the IME transition
    // is animated; many devices/OEM keyboards show it instantly with no
    // animation, so those events never arrive and the nav never hides. The
    // visualViewport fallback below reacts to the actual WebView resize
    // (SystemBars pads it natively either way), so run it on Android too.
    if (isHybridApp() && !isAndroid()) {
      return;
    }

    if ("virtualKeyboard" in navigator) {
      this.setupVirtualKeyboardAPI();
    } else {
      this.setupVisualViewportFallback();
    }
  };

  // Layer 1: VirtualKeyboard API (Chromium 94+)
  // Sets overlaysContent so CSS env(keyboard-inset-height) provides real-time
  // animated keyboard height — no JS needed for the layout value.
  // JS still handles class toggles, stored height, cubit state, and scroll.
  private setupVirtualKeyboardAPI(): void {
    const vk = (navigator as Navigator & { virtualKeyboard: VirtualKeyboard })
      .virtualKeyboard;
    vk.overlaysContent = true;
    document.body.classList.add("vk-native");

    vk.addEventListener("geometrychange", () => {
      const height = vk.boundingRect.height;
      const isOpen = height > 0;

      if (this.state.keyboardHeight === height) {
        return;
      }

      // Don't set --keyboard-height: CSS env(keyboard-inset-height) handles it
      document.body.classList.toggle("keyboard-open", isOpen);

      if (isOpen) {
        document.body.style.setProperty(
          "--stored-keyboard-height",
          `${height}px`
        );
      }

      this.emit({ keyboardHeight: height, isOpen });

      if (isOpen) {
        setTimeout(() => this.scrollInputIntoView());
      }
    });
  }

  // Layer 2: VisualViewport + focus tracking (Safari, Firefox)
  // Compares current visualViewport.height against a saved baseline.
  // Uses focus tracking to distinguish keyboard from browser chrome changes.
  private setupVisualViewportFallback(): void {
    this.initialViewportHeight =
      window.visualViewport?.height ?? window.innerHeight;

    document.addEventListener("focusin", this.handleFocusIn);
    document.addEventListener("focusout", this.handleFocusOut);
    window.visualViewport?.addEventListener(
      "resize",
      this.handleViewportResize
    );
  }

  private handleFocusIn = (e: FocusEvent): void => {
    const isText = isTextInput(e.target);

    if (isText && this.focusOutTimer) {
      clearTimeout(this.focusOutTimer);
      this.focusOutTimer = null;
    }

    this.inputFocused = isText;
  };

  private handleFocusOut = (): void => {
    this.focusOutTimer = setTimeout(() => {
      this.inputFocused = false;
      this.applyWebKeyboardState(0);
    }, FOCUS_OUT_DELAY_MS);
  };

  private handleViewportResize = (): void => {
    const currentHeight = window.visualViewport?.height ?? window.innerHeight;

    if (!this.inputFocused) {
      // No text input focused — update the baseline height
      // (handles orientation changes, browser chrome collapse, etc.)
      // Only update when keyboard is fully closed to avoid capturing
      // intermediate values during keyboard dismiss animation.
      if (!this.state.isOpen) {
        this.initialViewportHeight = currentHeight;
      }
      return;
    }

    const heightDiff = this.initialViewportHeight - currentHeight;
    this.applyWebKeyboardState(
      heightDiff > KEYBOARD_THRESHOLD ? heightDiff : 0
    );
  };

  private applyWebKeyboardState(rawHeight: number): void {
    const isOpen = rawHeight > 0;
    // On Android, SystemBars already pads the WebView above the keyboard
    // natively, so reporting the keyboard height again here would double it.
    const height = isAndroid() ? 0 : rawHeight;

    if (this.state.isOpen === isOpen && this.state.keyboardHeight === height) {
      return;
    }

    document.body.style.setProperty("--keyboard-height", `${height}px`);
    document.body.classList.toggle("keyboard-open", isOpen);
    document.body.classList.toggle(
      "keyboard-open-android",
      isAndroid() && isOpen
    );

    if (isOpen) {
      document.body.style.setProperty(
        "--stored-keyboard-height",
        `${height}px`
      );
    }

    this.emit({ keyboardHeight: height, isOpen });

    if (isOpen) {
      setTimeout(() => this.scrollInputIntoView());
    }
  }
}
